import { FirstChar } from 'ia-react-core/~/interfaces/FirstChar';
import { Breakpoint } from 'ia-react-core/~/interfaces/Breakpoint';
import keys from 'ia-react-core/~/utils/accessors/keys';
import UNITS from '../../../constants/UNITS';
import BREAKPOINT_LIST from '../../../constants/BREAKPOINT_LIST';
import { WithSpacingProps } from './withSpacing';

const sides = ['top', 'right', 'bottom', 'left'] as const;

const directions = {
  h: ['left', 'right'],
  v: ['top', 'bottom'],
} as const;

type SpacingStyles = (typeof spacingStyles)[number];
type Direction = (typeof directions)[keyof typeof directions][number];

const spacingStyles = ['padding', 'margin'] as const;

// We build the list of responsive props once for performance
export const responsiveProps = spacingStyles.reduce((result, spacingType) => {
  const shortSpacingType = spacingType[0];
  BREAKPOINT_LIST.forEach((breakpoint) => {
    result.push(`${shortSpacingType}-${breakpoint}`);

    keys(directions).forEach((direction) => {
      result.push(`${shortSpacingType}-${direction}-${breakpoint}`);
    });

    sides.forEach((side) => {
      result.push(`${shortSpacingType}-${side[0]}-${breakpoint}`);
    });
  });
  return result;
}, []);

type Styles = {
  [P in SpacingStyles]?: number
} & {
  [P in `${SpacingStyles}${Capitalize<Direction>}`]?: number
};

type Behaviors = {
  [P in keyof WithSpacingProps]?: (value: number, styles: Styles) => void
};

export const behaviors: Behaviors = {};
addBehaviors(behaviors);

export const responsiveBehaviors: Behaviors = {};
BREAKPOINT_LIST.forEach((breakpoint) => {
  addBehaviors(responsiveBehaviors, `-${breakpoint}`);
});

function addBehaviors(result: Behaviors, suffix: '' | `-${Breakpoint}` = '') {
  spacingStyles.forEach((spacingType) => {
    const shortSpacingType = spacingType[0] as FirstChar<typeof spacingType>;

    result[`${shortSpacingType}${suffix}`] = (value: number, styles) => {
      validateUnit(shortSpacingType, value);
      styles[spacingType] = value;
    };

    keys(directions).forEach((direction) => {
      result[`${shortSpacingType}-${direction}${suffix}`] = (value: number, styles: Styles) => {
        validateUnit(shortSpacingType, value);
        const directionSides = directions[direction];
        directionSides.forEach((side) => {
          styles[`${spacingType}${capitalize(side)}`] = value;
        });
      };
    });

    sides.forEach((side) => {
      const shortSide = side[0] as FirstChar<typeof side>;
      const prop = `${shortSpacingType}-${shortSide}${suffix}` as const;
      result[`${shortSpacingType}-${shortSide}${suffix}`] = (value, styles) => {
        validateUnit(prop, value);
        styles[`${spacingType}${capitalize(side)}`] = value;
      };
    });
  });

  return result;
}

function validateUnit(prop: keyof WithSpacingProps, unit: number) {
  const absUnit = Math.abs(unit);
  const sign = Math.sign(unit);
  // We accept both undefined and 0
  if (unit && !UNITS.includes(absUnit)) {
    const closestUnit = UNITS.findIndex((u) => u > absUnit);

    const lowUnit = closestUnit - 1;
    const highUnit = (closestUnit !== -1) ? closestUnit : UNITS.length - 1;

    const units = [
      UNITS[lowUnit] * sign,
      UNITS[highUnit] * sign,
    ].filter((u) => u);

    if (sign === -1) {
      units.reverse();
    }

    throw new Error(`Unit "${unit}" is not valid for "${prop}". Closest units are: "${units.join('", "')}".`);
  }
}

function capitalize<S extends string>(word: S) {
  return word.substring(0, 1).toUpperCase() + word.substring(1) as Capitalize<S>;
}
